import { DropResult } from '@hello-pangea/dnd'
import { Lab } from '../../../../types/bloodWork'

interface LabDragDropHandlers {
  handleDragEnd: (result: DropResult) => void
}

export default function useLabDragDrop(
  lab: Lab,
  setLab: React.Dispatch<React.SetStateAction<Lab>>
): LabDragDropHandlers {
  function handlePanelDragEnd(result: DropResult) {
    // User dropped outside the list
    if (!result.destination) {
      return
    }

    // User dropped in the same position
    if (result.source.index === result.destination.index) {
      return
    }

    // Reorder the panels array
    const reorderedPanels = Array.from(lab.panels)
    const [movedPanel] = reorderedPanels.splice(result.source.index, 1)
    reorderedPanels.splice(result.destination.index, 0, movedPanel)

    // Update state
    setLab({
      ...lab,
      panels: reorderedPanels,
    })
  }

  function handleItemDragEnd(result: DropResult) {
    // User dropped outside a valid droppable area
    if (!result.destination) {
      return
    }

    // User dropped in the same position
    if (
      result.source.droppableId === result.destination.droppableId &&
      result.source.index === result.destination.index
    ) {
      return
    }

    // Extract panel ID from droppableId (format: "panel-items-{panelId}")
    const sourcePanelId = result.source.droppableId.replace('panel-items-', '')
    const destPanelId = result.destination.droppableId.replace(
      'panel-items-',
      ''
    )

    // Prevent dragging between panels (safety check)
    if (sourcePanelId !== destPanelId) {
      return
    }

    // Reorder items within the panel
    setLab({
      ...lab,
      panels: lab.panels.map((panel) => {
        if (panel.id !== sourcePanelId) return panel

        const reorderedItems = Array.from(panel.items)
        const [movedItem] = reorderedItems.splice(result.source.index, 1)
        reorderedItems.splice(result.destination!.index, 0, movedItem)

        return {
          ...panel,
          items: reorderedItems,
        }
      }),
    })
  }

  function handleDragEnd(result: DropResult) {
    if (result.type === 'PANEL_ITEM') {
      handleItemDragEnd(result)
    } else {
      handlePanelDragEnd(result)
    }
  }

  return {
    handleDragEnd,
  }
}
